import { VisitOutcome, VisitState } from '@/types/graphql';
import {
    CheckCircleOutline,
    EventAvailable,
    EventBusy,
    PendingOutlined,
    PersonRemove,
} from '@mui/icons-material';

import { CalendarEventClinician, ServicesForCardDisplay } from './types';

export enum EventStatus {
    NEEDS_ASSIGNMENT = 'NEEDS_ASSIGNMENT',
    NEEDS_TIME = 'NEEDS_TIME',
    SCHEDULED = 'SCHEDULED',
    COMPLETED = 'COMPLETED',
    CANCELED = 'CANCELED',
}

export const getStatusIcon = (eventStatus: EventStatus) => {
    const iconProps = { sx: { fontSize: '18px', color: '#1D1B20' } };
    switch (eventStatus) {
        case EventStatus.NEEDS_ASSIGNMENT:
            return <PersonRemove {...iconProps} />;
        case EventStatus.NEEDS_TIME:
            return <PendingOutlined {...iconProps} />;
        case EventStatus.SCHEDULED:
            return <EventAvailable {...iconProps} />;
        case EventStatus.COMPLETED:
            return <CheckCircleOutline {...iconProps} />;
        case EventStatus.CANCELED:
            return <EventBusy {...iconProps} />;
        default:
            return <EventAvailable {...iconProps} />;
    }
};

const SCHEDULED_OR_IN_PROGRESS_STATES: VisitState[] = [
    VisitState.SCHEDULED,
    VisitState.CONFIRMED,
    VisitState.EN_ROUTE,
    VisitState.AT_LOCATION,
];

/**
 * Determine EventStatus for a visit based on state, outcome, and appointment data
 *
 * Status mapping:
 * - CANCELED: outcome.PATIENT_CANCELED || outcome.PATIENT_LATE_CANCELED || outcome.PATIENT_DID_NOT_SHOW
 * - COMPLETED: state.ENDED || state.IN_REVIEW || outcome.COMPLETED
 * - NEEDS_TIME: UNSCHEDULED with service, clinician, date, but no time
 * - SCHEDULED: SCHEDULED, CONFIRMED, EN_ROUTE, or AT_LOCATION with service, clinician, date, and time
 * - NEEDS_ASSIGNMENT: default fallback
 */
export const getVisitEventStatus = (
    visitState: VisitState,
    visitOutcome: VisitOutcome,
    appointment: {
        services: ServicesForCardDisplay[];
        clinician: CalendarEventClinician | null;
        startDatetime: Date | null;
        endDatetime: Date | null;
    },
): EventStatus => {
    const hasService = appointment.services.length > 0;
    const hasClinician = appointment.clinician !== null;
    const hasTime = appointment.startDatetime !== null && appointment.endDatetime !== null;

    // 1. Check for CANCELED first (terminal state)
    if (
        visitOutcome === VisitOutcome.PATIENT_CANCELED ||
        visitOutcome === VisitOutcome.PATIENT_LATE_CANCELED ||
        visitOutcome === VisitOutcome.PATIENT_DID_NOT_SHOW
    ) {
        return EventStatus.CANCELED;
    }

    // 2. Check for COMPLETED (terminal state)
    if (
        visitState === VisitState.ENDED ||
        visitState === VisitState.IN_REVIEW ||
        visitOutcome === VisitOutcome.COMPLETED
    ) {
        return EventStatus.COMPLETED;
    }

    // 3. Check for NEEDS_TIME: UNSCHEDULED with service, clinician, date, but no time
    if (visitState === VisitState.UNSCHEDULED && hasService && hasClinician && !hasTime) {
        return EventStatus.NEEDS_TIME;
    }

    // 4. Check for SCHEDULED: SCHEDULED, CONFIRMED, or active states with service, clinician, date, and time
    if (
        SCHEDULED_OR_IN_PROGRESS_STATES.includes(visitState) &&
        hasService &&
        hasClinician &&
        hasTime
    ) {
        return EventStatus.SCHEDULED;
    }

    // 5. Default fallback
    return EventStatus.NEEDS_ASSIGNMENT;
};

export const readableEventStatus = (status: EventStatus) =>
    status
        .toLowerCase()
        .split('_')
        .map((s) => s.charAt(0).toUpperCase() + s.slice(1))
        .join(' ');
